import React from "react";
import { styled, keyframes } from "styled-components";
import RecipeCardSkeleton from "./RecipeCardSkeleton";

const shimmer = keyframes`
  0% { background-position: -600px 0; }
  100% { background-position: 600px 0; }
`;

const Shimmer = styled.div`
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colorSurfaceVariant} 25%,
    ${({ theme }) => theme.colorSurface} 50%,
    ${({ theme }) => theme.colorSurfaceVariant} 75%
  );
  background-size: 1200px 100%;
  animation: ${shimmer} 1.4s infinite linear;
  border-radius: ${(props) => props.radius || "8px"};
`;

const Line = styled(Shimmer)`
  height: ${(props) => props.height || "14px"};
  width: ${(props) => props.width || "100%"};
  border-radius: 6px;
`;

const Block = styled(Shimmer)`
  width: ${(props) => props.width || "100%"};
  height: ${(props) => props.height || "100%"};
`;

const Circle = styled(Shimmer)`
  width: ${(props) => props.size || "36px"};
  height: ${(props) => props.size || "36px"};
  border-radius: 50%;
  flex-shrink: 0;
`;

// ── Layout mirrors Recipe.jsx ───────────────────────────────────────────────

const Container = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px 24px 0px 24px;
  flex-grow: inherit;
  align-self: stretch;
  overflow: hidden;
`;

const Content = styled.div`
  overflow: auto;
  flex: 3;
  min-height: 0px;
  padding-bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Hr = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.colorSurface};
  margin: 3px 0;
`;

// Channel row
const ChannelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;
const ChannelLeft = styled.div`
  display: flex;
  gap: 20px;
`;
const ChannelText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 4px;
`;

// RecipeDetails cards
const DetailsCard = styled.div`
  background-color: ${({ theme }) => theme.colorSurfaceVariant};
  border-radius: 8px;
  padding: 8px 12px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 24px;
`;
const RecipeInfoRow = styled.div`
  display: flex;
  gap: 10px;
`;
const SecondaryInfo = styled.div`
  flex: 2;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

// Tags
const TagsRow = styled.div`
  display: flex;
  gap: 10px;
`;
const TagPill = styled(Shimmer)`
  height: 28px;
  width: 80px;
  border-radius: 8px;
`;

// Social icons
const SocialRow = styled.div`
  display: flex;
  gap: 12px;
  padding: 0 12px;
`;

// Sidebar
const Secondary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 402px;
  flex-shrink: 0;
  overflow: auto;
  padding-bottom: 24px;
`;

const RecipePageSkeleton = () => (
  <Container>
    <Content>
      {/* Video */}
      <Block style={{ aspectRatio: "16 / 9", height: "auto" }} radius="12px" />

      {/* Title + info */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Line height="22px" width="55%" />
        <Line height="14px" width="20%" />
      </div>

      {/* Channel row */}
      <ChannelRow>
        <ChannelLeft>
          <Circle size="50px" />
          <ChannelText>
            <Line height="16px" width="140px" />
            <Line height="12px" width="100px" />
            <Line height="12px" width="200px" />
          </ChannelText>
        </ChannelLeft>
        <Block height="30px" width="90px" radius="8px" />
      </ChannelRow>

      <Hr />

      {/* Description card */}
      <DetailsCard>
        <Line height="13px" width="100%" />
        <Line height="13px" width="90%" />
        <Line height="13px" width="60%" />
      </DetailsCard>

      {/* RecipeInfo row */}
      <RecipeInfoRow>
        {/* Ingredients */}
        <DetailsCard style={{ flex: 3 }}>
          <CardHeader>
            <Line height="14px" width="100px" />
            <Line height="14px" width="80px" />
          </CardHeader>
          {Array.from({ length: 6 }).map((_, i) => (
            <Line key={i} height="16px" width={`${70 - i * 5}%`} />
          ))}
        </DetailsCard>

        {/* Time + Nutrition */}
        <SecondaryInfo>
          <DetailsCard>
            <CardHeader>
              <Line height="14px" width="80px" />
              <Line height="14px" width="60px" />
            </CardHeader>
            <Line height="16px" width="80%" />
            <Line height="16px" width="75%" />
            <Line height="16px" width="70%" />
          </DetailsCard>
          <DetailsCard>
            <CardHeader>
              <Line height="14px" width="80px" />
              <Line height="14px" width="70px" />
            </CardHeader>
            <Line height="16px" width="60%" />
            <Line height="16px" width="70%" />
            <Line height="16px" width="55%" />
            <Line height="16px" width="50%" />
          </DetailsCard>
        </SecondaryInfo>
      </RecipeInfoRow>

      {/* Tags */}
      <TagsRow>
        {Array.from({ length: 4 }).map((_, i) => <TagPill key={i} />)}
      </TagsRow>

      <Hr />

      {/* Social sharing */}
      <SocialRow>
        {Array.from({ length: 8 }).map((_, i) => (
          <Circle key={i} size="32px" />
        ))}
      </SocialRow>

      <Hr />

      {/* Comments placeholder */}
      <Line height="14px" width="30%" />
      <Line height="40px" width="100%" radius="8px" />
    </Content>

    {/* Sidebar */}
    <Secondary>
      {/* Instructions placeholder */}
      <Block height="160px" radius="8px" />
      {/* Recommended sm cards */}
      {Array.from({ length: 7 }).map((_, i) => (
        <RecipeCardSkeleton key={i} type="sm" />
      ))}
    </Secondary>
  </Container>
);

export default RecipePageSkeleton;
